import {
  useState,
  useEffect,
  createContext,
  useContext,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  GoogleAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth'
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import {
  loginSchema,
  signupSchema,
  userSchema,
  type LoginData,
  type SignupData,
} from '@/lib/validation'
import type { User, AuthState } from '@/types'

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthContextValue {
  user: User | null
  loading: boolean
  error: Error | null
  signIn: (data: LoginData) => Promise<void>
  signUp: (data: SignupData) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const getFallbackDisplayName = (fbUser: FirebaseUser): string => {
  if (fbUser.displayName && fbUser.displayName.trim()) {
    return fbUser.displayName
  }
  const email = fbUser.email || ''
  const fromEmail = email.includes('@') ? email.split('@')[0] : ''
  return fromEmail || 'User'
}

const firebaseUserToUser = (fbUser: FirebaseUser): User => {
  const candidate = {
    uid: fbUser.uid,
    email: fbUser.email || '',
    displayName: getFallbackDisplayName(fbUser),
    createdAt: new Date(fbUser.metadata.creationTime || Date.now()),
  }

  const parsed = userSchema.safeParse(candidate)
  if (!parsed.success) {
    throw new Error('Invalid user data from Firebase')
  }

  return parsed.data
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  })

  const ensureUserDocument = async (fbUser: FirebaseUser) => {
    const userRef = doc(db, 'users', fbUser.uid)
    const existing = await getDoc(userRef)
    if (existing.exists()) return

    await setDoc(userRef, {
      email: fbUser.email || '',
      displayName: getFallbackDisplayName(fbUser),
      createdAt: serverTimestamp(),
    })
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (fbUser) => {
        try {
          setState({
            user: fbUser ? firebaseUserToUser(fbUser) : null,
            loading: false,
            error: null,
          })
        } catch (error) {
          setState({ user: null, loading: false, error: error as Error })
        }
      },
      (error) => {
        setState({ user: null, loading: false, error: error as Error })
      }
    )

    return unsubscribe
  }, [])

  const signIn = async (data: LoginData) => {
    try {
      const validated = loginSchema.parse(data)
      setState((prev) => ({ ...prev, error: null }))
      await signInWithEmailAndPassword(
        auth,
        validated.email,
        validated.password
      )
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error }))
      throw error
    }
  }

  const signUp = async (data: SignupData) => {
    try {
      const validated = signupSchema.parse(data)
      setState((prev) => ({ ...prev, error: null }))

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        validated.email,
        validated.password
      )

      // Update Firebase Auth profile with display name
      await updateProfile(userCredential.user, {
        displayName: validated.displayName,
      })

      // Reload the user to get the updated profile
      await userCredential.user.reload()

      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: validated.email,
        displayName: validated.displayName,
        createdAt: serverTimestamp(),
      })

      // Force trigger auth state change with updated user
      setState((prev) => {
        const user = firebaseUserToUser(auth.currentUser!)
        return { ...prev, user }
      })
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error }))
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      setState((prev) => ({ ...prev, error: null }))
      const provider = new GoogleAuthProvider()
      const credential = await signInWithPopup(auth, provider)
      await ensureUserDocument(credential.user)
      setState((prev) => {
        const user = firebaseUserToUser(auth.currentUser!)
        return { ...prev, user }
      })
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error }))
      throw error
    }
  }

  const signOut = async () => {
    try {
      setState((prev) => ({ ...prev, error: null }))
      await firebaseSignOut(auth)
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error }))
      throw error
    }
  }

  const refreshUser = async () => {
    if (!auth.currentUser) return

    try {
      await auth.currentUser.reload()
      setState((prev) => ({
        ...prev,
        user: firebaseUserToUser(auth.currentUser!),
      }))
    } catch (error) {
      setState((prev) => ({ ...prev, error: error as Error }))
      throw error
    }
  }

  const contextValue: AuthContextValue = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    refreshUser,
  }

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
